import { type FC, useEffect, useRef, useState } from "react"
import * as TrinityAPI from "@/requests/TrinityAPI"
import { ICurrency } from "@/types/dict/ICurrency"
import clsx from "clsx"
import style from "./style.module.scss"

interface Props {
  amount: string
  currency: string
  onAmountChange: (v: string) => void
  onCurrencyChange: (code: string) => void
  balanceHint?: string
  readonly?: boolean
  placeholder?: string
  inBlock?: boolean
  error?: boolean
  disabledCurrency?: string
}

export const InlineCurrencyAmountInput: FC<Props> = ({
  amount,
  currency,
  onAmountChange,
  onCurrencyChange,
  balanceHint,
  readonly = false,
  placeholder = "0.00",
  inBlock = false,
  error = false,
  disabledCurrency
}) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [suggestions, setSuggestions] = useState<ICurrency[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        closeDropdown()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const closeDropdown = () => {
    setIsOpen(false)
    setSearchQuery("")
    setSuggestions([])
  }

  const openDropdown = () => {
    setIsOpen(true)
    TrinityAPI.suggestCurrency(currency, response => {
      if (response.status === 200) setSuggestions(response.data)
    })
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    TrinityAPI.suggestCurrency(value, response => {
      if (response.status === 200) setSuggestions(response.data)
    })
  }

  const handlePick = (c: ICurrency) => {
    if (c.code === disabledCurrency) return
    onCurrencyChange(c.code)
    closeDropdown()
  }

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/,/g, ".").replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1")
    onAmountChange(val)
  }

  return (
    <div className={clsx(style.wrapper, { [style.wrapperInBlock]: inBlock })}>
      <div className={clsx(style.inputRow, { [style.inputRowInBlock]: inBlock, [style.inputRowError]: error })}>
        <input
          type="text"
          className={clsx(style.amountInput, { [style.readonly]: readonly, [style.amountInputInBlock]: inBlock })}
          placeholder={placeholder}
          value={amount}
          readOnly={readonly}
          onChange={handleAmountChange}
        />
        <div className={style.currencyPickerContainer} ref={dropdownRef}>
          <button
            type="button"
            className={clsx(style.currencyButton, { [style.readonly]: readonly })}
            onClick={readonly ? undefined : isOpen ? closeDropdown : openDropdown}
          >
            {currency || "—"}
            {!readonly && <span className={style.arrow} />}
          </button>
          {isOpen && (
            <div className={style.dropdown}>
              <input
                type="text"
                className={style.searchInput}
                placeholder="Поиск валюты..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                autoFocus
              />
              <ul className={style.suggestionList}>
                {suggestions.map((c, i) => (
                  <li
                    key={i}
                    className={clsx(style.suggestionItem, {
                      [style.suggestionItemDisabled]: c.code === disabledCurrency
                    })}
                    onClick={() => handlePick(c)}
                  >
                    {c.code}
                    {c.name ? ` — ${c.name}` : ""}
                  </li>
                ))}
                {suggestions.length === 0 && <li className={style.noResults}>Не найдено</li>}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className={clsx(style.balanceHint, { [style.balanceHintError]: error })}>{balanceHint ?? ""}</div>
    </div>
  )
}
